import { ChangeEvent, FormEvent } from 'react';
import classNames from 'classnames';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';

import { locales } from 'constants/index';

import SkeletonLoader from '../SkeletonLoader';

import { FilterBarProps, FilterBarSearch, FilterControl } from './FilterBar.types';

import './filter-bar.scss';

/*
 * Config-driven filter bar for the list pages.
 *
 * One quiet line. The lookups people make most - by ID - sit in a single
 * segmented box, each segment labelled inline and sized for its value, with
 * Search as the box's last segment. Every other filter is a chip that carries
 * its own label and value ("Status  All"), as wide as its content; a chip that
 * narrows the list fills with the accent tint, so what is filtered reads at a
 * glance. Clear filters appears once anything is set.
 *
 * `advanced` controls join the chips: they fit on the line, so there is no
 * panel to open.
 */

const ANY = new Set<unknown>(['', 'All', null, undefined]);

// Whether a control currently narrows the list. A cleared date range comes
// back as [Invalid Date, Invalid Date].
function isActive(control: FilterControl): boolean {
  if (control.kind === 'custom') return false;

  const { value } = control as { value: unknown };
  const isDate = (entry: unknown) =>
    entry instanceof Date && !Number.isNaN(entry.getTime());

  if (Array.isArray(value)) return value.some(isDate);
  if (value instanceof Date) return isDate(value);

  return !ANY.has(value);
}

const labelOf = (control: FilterControl) =>
  control.kind === 'custom'
    ? ''
    : control.label || control.placeholder || 'Date';

function SearchBox({ search }: { search: FilterBarSearch }) {
  const { inputs, onChange, onSubmit, searchLabel } = search;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.name, event.target.value);
  };

  return (
    <form className="filter-bar__search" onSubmit={handleSubmit} role="search">
      {inputs.map((input) => (
        <div key={input.name} className="filter-bar__segment">
          <label htmlFor={input.name} className="filter-bar__segment-label">
            {input.label || input.placeholder}
          </label>
          {/* A placeholder repeating the label beside it is only noise. */}
          <InputText
            id={input.name}
            name={input.name}
            className="filter-bar__segment-input"
            placeholder={input.label ? input.placeholder : undefined}
            value={input.value}
            onChange={handleChange}
            style={
              input.fieldWidth ? { width: `${input.fieldWidth - 4}rem` } : undefined
            }
            autoComplete="off"
          />
        </div>
      ))}

      <button type="submit" className="filter-bar__search-submit">
        {searchLabel || locales.general.search}
      </button>
    </form>
  );
}

function Chip({ control }: { control: FilterControl }) {
  if (control.kind === 'custom') return <>{control.render}</>;

  const label = labelOf(control);
  const active = isActive(control);
  const id = `filter-${control.name || 'date'}`;

  if (control.kind === 'dropdown') {
    // Every list offers "All", so a chip always has a value to show and a
    // single filter can be cleared on its own.
    const options =
      control.required || control.options.some((option) => ANY.has(option.value))
        ? control.options
      : [{ key: 'all', label: 'All', value: '' }, ...control.options];

    return (
      <Dropdown
        inputId={id}
        ariaLabel={label}
        className={classNames('filter-chip', { 'filter-chip--active': active })}
        panelClassName="filter-chip__panel"
        value={control.value ?? ''}
        options={options}
        onChange={control.onChange}
        // The chip shows its label with the value, so a reader never has to
        // guess what "All" refers to.
        valueTemplate={(option) => (
          <span className="filter-chip__content">
            <span className="filter-chip__label">{label}</span>
            <span className="filter-chip__value">
              {!option || ANY.has(option.value) ? 'All' : option.label}
            </span>
          </span>
        )}
      />
    );
  }

  return (
    <div
      className={classNames('filter-chip filter-chip--date', {
        'filter-chip--active': active,
      })}
    >
      <label htmlFor={id} className="filter-chip__label">
        {label}
      </label>
      <Calendar
        inputId={id}
        className="filter-chip__calendar"
        placeholder="Any"
        selectionMode={control.selectionMode || 'range'}
        dateFormat={control.dateFormat || 'dd/mm/yy'}
        minDate={control.minDate}
        maxDate={control.maxDate}
        onChange={control.onChange}
        value={control.value}
        readOnlyInput
      />
      <i className="pi pi-calendar filter-chip__icon" aria-hidden="true" />
    </div>
  );
}

export default function FilterBar({
  loading = false,
  search,
  filters = [],
  advanced = [],
  onReset,
  className,
}: FilterBarProps) {
  const chips = [...filters, ...advanced];
  const hasActive =
    chips.some(isActive) ||
    !!search?.inputs.some((input) => input.value.trim().length > 0);

  if (loading) {
    return (
      <div className={classNames('filter-bar', className)}>
        <SkeletonLoader height="3.6rem" />
      </div>
    );
  }

  return (
    <div className={classNames('filter-bar', className)}>
      {search && <SearchBox search={search} />}

      {chips.length > 0 && (
        <div className="filter-bar__chips">
          {chips.map((control) => (
            <Chip key={control.name || 'date'} control={control} />
          ))}
        </div>
      )}

      {onReset && hasActive && (
        <button type="button" className="filter-bar__clear" onClick={onReset}>
          Clear filters
        </button>
      )}
    </div>
  );
}
